import { useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Mail, Send, HelpCircle, CheckCircle2 } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

export default function Contact() {
  const [form, setForm] = useState({ parent_name: "", child_name: "", email: "", phone: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.parent_name || !form.email || !form.subject || !form.message) {
      toast.error("Merci de remplir tous les champs obligatoires");
      return;
    }
    setLoading(true);
    try {
      await api.post("/messages", form);
      setSent(true);
      toast.success("Message envoyé avec succès !");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Une erreur est survenue");
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="max-w-xl mx-auto px-4 py-20 text-center" data-testid="contact-success">
        <CheckCircle2 className="h-16 w-16 text-[#16a34a] mx-auto" />
        <h1 className="mt-6 text-3xl font-bold text-[#0F172A]" style={{ fontFamily: "Outfit" }}>Message envoyé !</h1>
        <p className="mt-3 text-[#475569]">
          Merci {form.parent_name}. Nous vous répondrons par email ({form.email}) sous 48 heures ouvrées.
        </p>
        <div className="mt-8 flex justify-center gap-3">
          <Link to="/" className="px-5 py-2.5 rounded-md bg-[#1E3A8A] text-white font-semibold hover:bg-[#1E40AF] transition-colors">
            Retour à l'accueil
          </Link>
          <Link to="/faq" className="px-5 py-2.5 rounded-md border border-[#E2E8F0] text-[#1E3A8A] font-semibold hover:bg-slate-50 transition-colors">
            Voir la FAQ
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-14 grid lg:grid-cols-5 gap-10" data-testid="contact-page">
      <div className="lg:col-span-2">
        <span className="text-xs tracking-[0.3em] uppercase text-[#1E3A8A] font-bold">Nous écrire</span>
        <h1 className="mt-2 text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#0F172A] tracking-tighter" style={{ fontFamily: "Outfit" }}>
          Une question ?<br /><span className="text-[#D97706]">On vous répond.</span>
        </h1>
        <div className="mt-6 p-5 bg-[#FEF3C7] border border-[#D97706]/30 rounded-lg">
          <div className="flex items-start gap-3">
            <HelpCircle className="h-5 w-5 text-[#92400E] shrink-0 mt-0.5" />
            <div>
              <div className="font-bold text-[#92400E]" style={{ fontFamily: "Outfit" }}>Avant d'envoyer...</div>
              <p className="mt-1 text-sm text-[#92400E]/90">
                Votre question a peut-être déjà sa réponse dans notre FAQ.
              </p>
              <Link to="/faq" className="mt-2 inline-flex items-center gap-1 text-sm font-bold text-[#92400E] hover:underline">
                Consulter la FAQ →
              </Link>
            </div>
          </div>
        </div>
      </div>

      <form onSubmit={submit} className="lg:col-span-3 space-y-4 bg-white border border-[#E2E8F0] rounded-lg p-6 lg:p-8" data-testid="contact-form">
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="parent_name">Votre nom *</Label>
            <Input id="parent_name" value={form.parent_name} onChange={update("parent_name")} required data-testid="input-parent-name" />
          </div>
          <div>
            <Label htmlFor="child_name">Nom de l'enfant</Label>
            <Input id="child_name" value={form.child_name} onChange={update("child_name")} data-testid="input-child-name" />
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="email">Email *</Label>
            <Input id="email" type="email" value={form.email} onChange={update("email")} required data-testid="input-email" />
          </div>
          <div>
            <Label htmlFor="phone">Téléphone</Label>
            <Input id="phone" type="tel" value={form.phone} onChange={update("phone")} data-testid="input-phone" />
          </div>
        </div>
        <div>
          <Label htmlFor="subject">Sujet *</Label>
          <Input id="subject" value={form.subject} onChange={update("subject")} required data-testid="input-subject" />
        </div>
        <div>
          <Label htmlFor="message">Votre message *</Label>
          <Textarea id="message" rows={6} value={form.message} onChange={update("message")} required data-testid="input-message" />
        </div>
        <Button
          type="submit"
          disabled={loading}
          data-testid="contact-submit"
          className="w-full bg-[#1E3A8A] hover:bg-[#1E40AF] text-white font-semibold h-12"
        >
          {loading ? "Envoi..." : (<span className="flex items-center gap-2"><Send size={16} /> Envoyer le message</span>)}
        </Button>
        <p className="text-xs text-[#475569] text-center">
          <Mail size={10} className="inline" /> Réponse sous 48h ouvrées · champs marqués d'un * obligatoires
        </p>
      </form>
    </div>
  );
}
